import {Channel} from "./Channel";
import useStore from "../store.ts";
import {Subscription} from "./Subscription.ts";
import {postClearAll} from "../worker/DataWorkerManager.ts";
import {getChannelSubscriptionFields} from "../utils/ChannelFieldCollector.ts";

export class ChannelManager {
    private channels: Map<string, Channel>
    /* see docs/EXTRACTED_COMMENTS.md#c715 */
    private fullResyncPending = false;

    constructor() {
        this.channels = new Map();
    }

    /** Taint retained channel data after a sudden disconnect. */
    markFullResyncNeeded(): void {
        this.fullResyncPending = true;
    }

    addChannel(
        id: string,
        channelName: string | number,
        subscription: Subscription,
    ): Channel | null {
        const store = useStore();
        channelName = `${channelName}`;
        if (this.channels.has(channelName)) {
            const channel = <Channel>this.channels.get(channelName);
            channel.addSubscription(id, subscription);
            return <Channel>this.channels.get(channelName);
        }
        if (!store.socketManager) return null;
        const requestedFields = getChannelSubscriptionFields(channelName);
        store.socketManager.requestChannel(channelName, requestedFields);
        const channel = new Channel(channelName);
        channel.addSubscription(id, subscription);
        this.channels.set(channelName, channel);
        return channel;
    }
    /* see docs/EXTRACTED_COMMENTS.md#c320 */
    async clearChannels(keep_channels: boolean = false) {
        this.fullResyncPending = false;
        await postClearAll();

        const store = useStore();
        for (const channel of [...this.channels.values()]) {
            const name = channel.getName();
            if (!keep_channels) {
                channel.clearSubscriptions();
                this.channels.delete(name);
                if (store.socketManager) {
                    store.socketManager.removeChannel(name);
                }
            } else {
                channel.setRows([]);
                channel.setExpectedRows(-1);
                channel.markBootstrapping();
            }
        }
    }

    listActiveChannels(): string[] {
        return [...this.channels.keys()];
    }

    /* see docs/EXTRACTED_COMMENTS.md#c321 */
    clearAllSubscriptions(): void {
        for (const channel of this.channels.values()) {
            channel.clearSubscriptions();
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c322 */
    removeSubscriptionFromAllChannels(componentId: string): number {
        let removed = 0;
        for (const channel of this.channels.values()) {
            if (channel.removeSubscription(componentId)) removed++;
        }
        return removed;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c711 */
    markAllPending(): void {
        for (const channel of this.channels.values()) {
            channel.setExpectedRows(-1);
            channel.markBootstrapping();
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c324 */
    async syncToServer(): Promise<void> {
        const store = useStore();
        if (!store.socketManager) return;
        if (this.fullResyncPending) {
            // see docs/EXTRACTED_COMMENTS.md#c769
            store.socketManager.getUpdateQueueManager().clear();
            // see docs/EXTRACTED_COMMENTS.md#c716
            await this.clearChannels(true);
        }
        for (const name of this.listActiveChannels()) {
            store.socketManager.requestChannel(name, getChannelSubscriptionFields(name));
        }
    }

    getChannels(): Channel[] {
        return [...this.channels.values()];
    }

    getChannel(id: string | number): Channel | null {
        id = `${id}`;
        const channel = this.channels.get(id);
        if (!channel) return null;
        return channel;
    }

}
